import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { Send } from 'lucide-react';
import type { ContactFormConfig } from '../data/profile';
import type { ServiceOption } from '../data/services';
import { createLead, getLeadCaptureError } from '../services/leadCapture';
import { ServiceSelector } from './ServiceSelector';

type ContactFormProps = {
  form: ContactFormConfig;
  services: ServiceOption[];
};

const serviceRequiredMessage = 'Selecione um serviço para continuar.';

export function ContactForm({ form, services }: ContactFormProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [serviceError, setServiceError] = useState('');
  const [submitWarning, setSubmitWarning] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const selectedService = services.find((service) => service.id === selectedServiceId);

    if (!selectedService) {
      setServiceError(serviceRequiredMessage);
      return;
    }

    setSubmitWarning('');
    setIsSubmitting(true);

    const message = [
      form.messageIntro,
      '',
      `Nome: ${name.trim()}`,
      `Telefone: ${phone.trim()}`,
      `Serviço: ${selectedService.title}`
    ].join('\n');

    const whatsappUrl = `${form.whatsappBaseUrl}?text=${encodeURIComponent(message)}`;

    try {
      await createLead(form.leadEndpoint, {
        name: name.trim(),
        phone: phone.trim(),
        service: selectedService
      });
    } catch (error) {
      setSubmitWarning(`${getLeadCaptureError(error)} Seu atendimento ainda pode continuar pelo WhatsApp.`);
    } finally {
      setIsSubmitting(false);
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleServiceChange = (service: ServiceOption) => {
    setSelectedServiceId(service.id);
    setServiceError('');
    setSubmitWarning('');
  };

  return (
    <motion.form
      className="contact-form"
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.36, delay: 0.1, ease: 'easeOut' }}
    >
      <h2>{form.title}</h2>

      <label className="form-field">
        <span>{form.nameLabel}</span>
        <input
          type="text"
          name="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={form.namePlaceholder}
          autoComplete="name"
          required
        />
      </label>

      <label className="form-field">
        <span>{form.phoneLabel}</span>
        <input
          type="tel"
          name="phone"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          placeholder={form.phonePlaceholder}
          autoComplete="tel"
          inputMode="tel"
          required
        />
      </label>

      <ServiceSelector
        services={services}
        selectedServiceId={selectedServiceId}
        onChange={handleServiceChange}
        error={serviceError}
      />

      <label className="terms-control">
        <input
          type="checkbox"
          checked={acceptedTerms}
          onChange={(event) => setAcceptedTerms(event.target.checked)}
          required
        />
        <span>{form.terms}</span>
      </label>

      <button className="form-submit" type="submit" disabled={!acceptedTerms || isSubmitting}>
        <span>{isSubmitting ? 'Enviando...' : form.submitLabel}</span>
        <Send size={16} strokeWidth={1.9} />
      </button>

      {submitWarning ? <p className="form-feedback form-feedback--warning">{submitWarning}</p> : null}
    </motion.form>
  );
}
